import { useEffect, useMemo, useState } from "react";

import {
  Combobox,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
  Dialog,
  DialogBackdrop,
  DialogPanel,
} from "@headlessui/react";
import { ChevronRightIcon, MagnifyingGlassIcon } from "@heroicons/react/20/solid";
import { XMarkIcon } from "@heroicons/react/24/outline";

import useOmniSearch, { useOmniSearchVat } from "../../../data/useOmniSearch";
import { OmniSearchType } from "../../../models/search/OmniSearch";
import classNames from "../../../utils/classNames";
import StatusPill, { StatusPillColour } from "../StatusPill";
import { getTypeIcon } from "./SearchPaletteTypePill";
import {
  SearchPaletteItem,
  SearchPaletteItemType,
} from "./SearchPaletteTypes";
import openInNewTab from "../../../utils/openInNewTab";

interface SearchPaletteDialogProps {
  open: boolean;
  onClose: () => void;
  placeholder?: string;
  theme?: "light" | "dark";
  type?: SearchPaletteItemType;
  allowedTypes?: SearchPaletteItemType[];
  items?: SearchPaletteItem[] | null;
  onSelect?: (item: SearchPaletteItem) => void;
  keepOpenOnSelect?: boolean;
  searchOnly?: boolean;
  searchTerm?: string;
  setSearchTerm? : any;
  typeLocked?: boolean
}

export default function SearchPaletteDialog({
  open,
  onClose,
  placeholder = "Search...",
  theme = "light",
  type = undefined,
  allowedTypes,
  items = null,
  onSelect  = () => {},
  keepOpenOnSelect = false,
  searchOnly = false,
  searchTerm = undefined,
  setSearchTerm = undefined,
  typeLocked = false
}: SearchPaletteDialogProps) {
  const [query, setQuery] = useState("");
  const [selectedType, setSelectedType] = useState<SearchPaletteItemType | null>(type ?? "Stock");
  const [selectedPreviewItem, setSelectedPreviewItem] = useState<SearchPaletteItem | null>(null);
  const isTypeLocked =  typeLocked == undefined ? Boolean(type) : typeLocked;


  useEffect(() => {
    setQuery(searchTerm ?? "");
  }, [searchTerm]);
  useEffect(() => {
    setSelectedType(type ?? "Stock");
  }, [type]);

  useEffect(() => {
    if (!open) {
      setSelectedPreviewItem(null);
      return;
    }
  }, [open]);

  const availableTypes = useMemo(
    () => {
      const preferredOrder: SearchPaletteItemType[] = ["Stock", "Order", "Vehicle", "Lead", "User"];
      const defaultTypes: SearchPaletteItemType[] = ["Stock", "Order", "Vehicle", "Lead"];
      if (items == null) return allowedTypes?.length ? sortTypesByPreference(allowedTypes, preferredOrder) : defaultTypes;

      return sortTypesByPreference(
        Array.from(
        new Set(
          items
            .map((item) => item.type)
            .filter((itemType): itemType is SearchPaletteItemType => Boolean(itemType))
        )
      ),
      preferredOrder
      );
    },
    [items, allowedTypes]
  );

  const typedItems = useMemo(
    () => (items ?? []).filter((item) => selectedType == null ? true : item.type === selectedType),
    [items, selectedType]
  );

  const filteredItems = useMemo<SearchPaletteItem[]>(() => {
    if (!query.trim()) return [];
    return typedItems.filter((item) =>
      `${item.name} ${item.description ?? ""} ${item.meta ?? ""}`.toLowerCase().includes(query.toLowerCase())
    );
  }, [query, typedItems]);

  const recentItems = typedItems.slice(0, 5);
  const { omniSearch } = useOmniSearch(query, mapToOmniSearchType(selectedType), open && !items);

  // Part-backed results (Stock/Order) carry a VAT/GLOBAL scheme; resolve it in a
  // separate batched call so the search itself stays fast.
  const partBackedIds = useMemo(
    () =>
      (omniSearch.data?.data ?? [])
        .filter((item) => item.type === OmniSearchType.Stock || item.type === OmniSearchType.Order)
        .map((item) => item.id),
    [omniSearch.data]
  );

  const { omniSearchVat } = useOmniSearchVat(partBackedIds, open && !items);

  const schemeById = useMemo(() => {
    const map = new Map<string, "VAT" | "GLOBAL">();
    for (const result of omniSearchVat.data?.data ?? []) {
      if (result.scheme) map.set(result.id, result.scheme);
    }
    return map;
  }, [omniSearchVat.data]);

  const remoteItems = useMemo<SearchPaletteItem[]>(
    () =>
      (omniSearch.data?.data ?? []).map((item) => ({
        id: item.id,
        name: item.name,
        description: item.description ?? undefined,
        meta: item.type != null ? omniSearchTypeToLabel(item.type) : undefined,
        type: mapToSearchType(item.type),
        url: item.url ?? undefined,
        scheme: schemeById.get(item.id) ?? null,
      })),
    [omniSearch.data, schemeById]
  );

  const closePalette = () => {
    setQuery("");
    setSelectedPreviewItem(null);
    onClose();
  };

  const isRemoteMode = items == null;
  const isLoadingRemote = isRemoteMode && omniSearch.isLoading;
  const visibleItems = useMemo(
    () => (isRemoteMode ? remoteItems : (query === "" ? recentItems : filteredItems)),
    [isRemoteMode, remoteItems, query, recentItems, filteredItems]
  );

  useEffect(() => {
    if (!open) return;

    if (!selectedPreviewItem) {
      setSelectedPreviewItem(visibleItems[0] ?? null);
      return;
    }

    const existsInVisibleItems = visibleItems.some((item) => String(item.id) === String(selectedPreviewItem.id));
    if (!existsInVisibleItems) {
      setSelectedPreviewItem(visibleItems[0] ?? null);
    }
  }, [open, visibleItems, selectedPreviewItem]);

  const mode = {
    dialogBackdrop: theme === "dark" ? "bg-gray-900/70" : "bg-gray-900/40",
    dialogPanel:
      theme === "dark"
        ? "bg-gray-900 outline-white/10"
        : "bg-white outline-gray-200",
    input:
      theme === "dark"
        ? "bg-gray-900 text-white placeholder:text-gray-500 border-gray-700"
        : "bg-white text-gray-900 placeholder:text-gray-500 border-gray-300",
    icon: theme === "dark" ? "text-gray-500" : "text-gray-400",
    title: theme === "dark" ? "text-white" : "text-gray-900",
    subtitle: theme === "dark" ? "text-gray-300" : "text-gray-500",
    shortcut:
      theme === "dark"
        ? "bg-gray-800 text-gray-300 ring-gray-700"
        : "bg-gray-50 text-gray-500 ring-gray-200",
    typeCard:
      theme === "dark"
        ? "border-gray-700 bg-gray-800/50 text-gray-200 hover:bg-gray-800"
        : "border-gray-200 bg-white text-gray-700 hover:bg-gray-50",
    typeCardActive:
      theme === "dark"
        ? "border-primary-400 bg-primary-500/10 text-white ring-1 ring-primary-400/40"
        : "border-primary-300 bg-primary-50 text-primary-700 ring-1 ring-primary-200",
    sectionBorder: theme === "dark" ? "border-gray-700" : "border-gray-200",
    divider: theme === "dark" ? "border-gray-700" : "border-gray-200",
    listItem:
      theme === "dark"
        ? "border-gray-700 bg-gray-800/40 text-gray-200"
        : "border-gray-200 bg-white text-gray-800",
    listItemLeftDefault: theme === "dark" ? "border-l-gray-700" : "border-l-gray-200",
    listItemActive:
      theme === "dark"
        ? ""
        : "",
    option:
      theme === "dark"
        ? "text-gray-300 hover:bg-white/5 hover:text-white data-focus:bg-white/5 data-focus:text-white"
        : "text-gray-700 hover:bg-gray-100 hover:text-gray-900 data-focus:bg-gray-100 data-focus:text-gray-900",
    optionMeta: theme === "dark" ? "text-gray-500" : "text-gray-500",
    emptyTextTitle: theme === "dark" ? "text-white" : "text-gray-900",
    emptyTextBody: theme === "dark" ? "text-gray-400" : "text-gray-500",
    detailsTitle: theme === "dark" ? "text-white" : "text-gray-900",
    detailsBody: theme === "dark" ? "text-gray-300" : "text-gray-700",
    detailsMuted: theme === "dark" ? "text-gray-400" : "text-gray-500",
    detailsLink: theme === "dark" ? "text-indigo-400" : "text-primary-700",
    detailsButton:
      theme === "dark"
        ? "bg-indigo-500 hover:bg-indigo-400 text-white"
        : "bg-primary-600 hover:bg-primary-700 text-white",
    detailsSurface: theme === "dark" ? "bg-gray-900" : "bg-white",
  };

  const visibleTypeOptions = isTypeLocked ? [type!] : availableTypes;
  const dialogTitle = type ? `Select ${type.toLowerCase()}` : "Select item";
  const subtitleTypes = (allowedTypes?.length ? allowedTypes : ["Order", "Vehicle", "Stock", "Lead"]).map((x) =>
    x.toLowerCase()
  );
  const dialogSubtitle = type
    ? `Search and select a ${type.toLowerCase()}`
    : `Search and select ${subtitleTypes.join(", ")}`;
  const resolvedPlaceholder = placeholder !== "Search..."
    ? placeholder
    : selectedType
      ? `Search ${selectedType.toLowerCase()}s...`
      : "Search orders by order number, vehicle, customer...";

  return (
    <Dialog className="relative z-50" open={open} onClose={closePalette}>
      <DialogBackdrop
        transition
        className={classNames(
          "fixed inset-0 transition-opacity data-closed:opacity-0 data-enter:duration-200 data-enter:ease-out data-leave:duration-150 data-leave:ease-in",
          mode.dialogBackdrop
        )}
      />

      <div className="fixed inset-0 z-50 w-screen overflow-y-auto p-4 sm:p-6 md:p-20">
        <DialogPanel
          transition
          className={classNames(
            "mx-auto max-w-5xl transform overflow-hidden rounded-2xl shadow-2xl outline-1 -outline-offset-1 transition-all data-closed:scale-95 data-closed:opacity-0 data-enter:duration-200 data-enter:ease-out data-leave:duration-150 data-leave:ease-in",
            mode.dialogPanel
          )}
        >
          <Combobox<SearchPaletteItem>
            onChange={(item: SearchPaletteItem) => {
              setSelectedPreviewItem(item);
            }}
          >
            {() => {
              const selected = selectedPreviewItem ?? visibleItems[0] ?? null;
              // Re-resolve from the current items so late-arriving data (e.g. the
              // VAT/GLOBAL scheme) shows without needing to re-select the item.
              const previewItem: SearchPaletteItem | null = selected
                ? visibleItems.find((item) => String(item.id) === String(selected.id)) ?? selected
                : null;
              return (
                <>
                  <div className={classNames("border-b p-5 sm:p-6", mode.divider)}>
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <h2 className={classNames("text-3xl font-semibold tracking-tight", mode.title)}>{dialogTitle}</h2>
                        <p className={classNames("mt-1 text-base", mode.subtitle)}>{dialogSubtitle}</p>
                      </div>
                      <button
                        type="button"
                        onClick={closePalette}
                        className={classNames(
                          "rounded-md p-1 transition hover:bg-black/5 dark:hover:bg-white/10",
                          mode.icon
                        )}
                        aria-label="Close dialog"
                      >
                        <XMarkIcon className="h-5 w-5" />
                      </button>
                    </div>
                  </div>

                  <div className={classNames("border-b p-5 sm:p-6", mode.divider)}>
                    <div className="relative grid grid-cols-1">
                      <ComboboxInput
                        autoFocus
                        value={searchTerm}
                        className={classNames(
                          "col-start-1 row-start-1 h-12 w-full rounded-lg border pr-20 pl-10 text-base outline-none sm:text-base",
                          mode.input
                        )}
                        placeholder={resolvedPlaceholder}
                        onChange={(event) => {
                          if(searchTerm !== undefined) setSearchTerm(event.target.value);
                          else setQuery(event.target.value);
                        }}
                      />
                      <MagnifyingGlassIcon
                        className={classNames("pointer-events-none col-start-1 row-start-1 ml-3 size-5 self-center", mode.icon)}
                        aria-hidden="true"
                      />
                      {/* <span
                        className={classNames(
                          "pointer-events-none col-start-1 row-start-1 mr-3 inline-flex self-center justify-self-end rounded px-2 py-1 text-xs font-semibold ring-1 ring-inset",
                          mode.shortcut
                        )}
                      >
                        Ctrl K
                      </span> */}
                    </div>

                    <div
                      className={classNames(
                        "mt-4 gap-3",
                        isTypeLocked ? "flex" : "grid grid-cols-2 md:grid-cols-3"
                      )}
                    >
                      {visibleTypeOptions.map((itemType) => {
                        const Icon = getTypeIcon(itemType);
                        const active = selectedType === itemType || (isTypeLocked && type === itemType);

                        return (
                          <button
                            key={itemType}
                            type="button"
                            onClick={() => setSelectedType(itemType)}
                            disabled={isTypeLocked}
                            className={classNames(
                              "rounded-xl border p-4 text-left transition",
                              isTypeLocked ? "w-full max-w-xs" : "",
                              active ? getTypeTabActiveClasses(itemType, theme) : mode.typeCard,
                              isTypeLocked ? "cursor-default" : "cursor-pointer"
                            )}
                          >
                            <div className="flex items-center gap-3">
                              <span
                                className={classNames(
                                  "inline-flex h-9 w-9 items-center justify-center rounded-lg",
                                  active ? getTypeTabIconClasses(itemType, theme) : "bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-300"
                                )}
                              >
                                <Icon className="h-5 w-5" />
                              </span>
                              <div className="min-w-0">
                                <p className="text-lg font-semibold">{itemType !== "Stock" ? `${itemType}s` : itemType}</p>
                                <p className={classNames("truncate text-sm", mode.subtitle)}>
                                  Search by {itemType.toLowerCase()}
                                </p>
                              </div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {(items == null || query === "" || filteredItems.length > 0) && (
                    <ComboboxOptions
                      as="div"
                      static
                      className={classNames("flex h-[28rem] transform-gpu border-t", mode.sectionBorder)}
                    >
                      <div className="h-full min-w-0 flex-auto overflow-y-auto p-4 sm:p-5">
                        {isLoadingRemote ? (
                          <div className="flex h-full min-h-[16rem] items-center justify-center">
                            <div className="h-8 w-8 animate-spin rounded-full border-2 border-gray-200 border-t-primary-600" />
                          </div>
                        ) : (
                          <div className="-mx-1 space-y-2 text-sm">
                            {visibleItems.map((item) => (
                              <ComboboxOption
                                as="div"
                                key={item.id}
                                value={item}
                                onClick={() => setSelectedPreviewItem(item)}
                                className={({ focus }) =>
                                  classNames(
                                    "group cursor-pointer rounded-xl border border-l-4 p-3 select-none transition",
                                    mode.listItem,
                                    mode.listItemLeftDefault,
                                    focus ? mode.listItemActive : "",
                                    selectedPreviewItem?.id === item.id ? "border-l-primary-600" : "",
                                    getItemTypeHoverClasses(item.type, theme)
                                  )
                                }
                              >
                                <div className="flex items-center gap-3">
                                  <span
                                    className={classNames(
                                      "inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg",
                                      getItemTypeIconSquareClasses(item.type, theme)
                                    )}
                                  >
                                    {(() => {
                                      const Icon = getTypeIcon(item.type ?? "Vehicle");
                                      return <Icon className="h-5 w-5" />;
                                    })()}
                                  </span>
                                  <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-2">
                                      <p className="truncate text-xl font-semibold leading-tight">{item.name}</p>
                                      {item.scheme && (
                                        <StatusPill text={item.scheme} colour={getSchemeColour(item.scheme)} />
                                      )}
                                    </div>
                                    {(item.description || item.meta) && (
                                      <p className={classNames("truncate text-sm", mode.optionMeta)}>
                                        {[item.description, item.meta].filter(Boolean).join(" ")}
                                      </p>
                                    )}
                                  </div>
                                  <ChevronRightIcon className={classNames("size-6 flex-none", mode.icon)} aria-hidden="true" />
                                </div>
                              </ComboboxOption>
                            ))}
                          </div>
                        )}
                      </div>

                      {previewItem && (
                        <div
                          className={classNames(
                            "hidden h-full w-[24rem] flex-none flex-col border-l overflow-y-auto p-6 sm:flex",
                            mode.sectionBorder,
                            mode.detailsSurface
                          )}
                        >
                          <div className="text-center">
                            <div
                              className={classNames(
                                "mx-auto flex h-16 w-16 items-center justify-center rounded-full",
                                getItemTypeIconSquareClasses(previewItem.type, theme)
                              )}
                            >
                              {(() => {
                                const Icon = getTypeIcon(previewItem.type ?? "Vehicle");
                                return <Icon className="h-8 w-8" />;
                              })()}
                            </div>
                            <h2 className={classNames("mt-4 text-2xl font-semibold leading-tight", mode.detailsTitle)}>
                              {previewItem.name}
                            </h2>
                            <p className={classNames("mt-3 text-lg leading-snug", mode.detailsMuted)}>
                              {previewItem.description ?? previewItem.meta ?? "Selection details"}
                            </p>
                          </div>

                          <div className="mt-8 flex flex-1 flex-col justify-between">
                            <dl className={classNames("grid grid-cols-[88px_1fr] gap-y-4 text-base", mode.detailsBody)}>
                              <dt className={classNames("font-semibold", mode.detailsTitle)}>Type</dt>
                              <dd>{previewItem.type ?? "-"}</dd>

                              {previewItem.scheme && (
                                <>
                                  <dt className={classNames("font-semibold", mode.detailsTitle)}>Scheme</dt>
                                  <dd>
                                    <StatusPill text={previewItem.scheme} colour={getSchemeColour(previewItem.scheme)} />
                                  </dd>
                                </>
                              )}

                              {!searchOnly && (
                                <>
                                  <dt className={classNames("font-semibold", mode.detailsTitle)}>Link</dt>
                                  <dd className="truncate">
                                    {previewItem.url ? (
                                      <div className="cursor-pointer hover:text-primary transition-colors duration-200" onClick={() => openInNewTab(`${previewItem.url}/${previewItem.id}`)}>
                                        Open in new tab
                                      </div>
                                    ) : (
                                      "-"
                                    )}
                                  </dd>
                                </>
                              )}
  
                            </dl>
                            <button
                              type="button"
                              className={classNames(
                                "mt-6 w-full rounded-lg px-4 py-3 text-base font-semibold shadow-sm",
                                mode.detailsButton
                              )}
                              disabled={!previewItem}
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={() => {
                                if (!previewItem) return;
                                if(searchOnly){
                                  openInNewTab(`${previewItem.url}/${previewItem.id}`)
                                  return;
                                }
                                  
                                onSelect(previewItem);
                                if (!keepOpenOnSelect) closePalette();
                              }}
                            >
                              {searchOnly ? `Search ${previewItem.type ?? "item"}` : `Add ${previewItem.type ?? "item"}`}
                            </button>
                          </div>
                        </div>
                      )}
                    </ComboboxOptions>
                  )}

                  {items != null && query !== "" && filteredItems.length === 0 && (
                    <div className="px-6 py-14 text-center text-sm sm:px-14">
                      <MagnifyingGlassIcon className={classNames("mx-auto size-6", mode.icon)} aria-hidden="true" />
                      <p className={classNames("mt-4 font-semibold", mode.emptyTextTitle)}>No results found</p>
                      <p className={classNames("mt-2", mode.emptyTextBody)}>
                        We could not find anything with that term. Please try again.
                      </p>
                    </div>
                  )}
                </>
              );
            }}
          </Combobox>
        </DialogPanel>
      </div>
    </Dialog>
  );
}

function sortTypesByPreference(types: SearchPaletteItemType[], preferredOrder: SearchPaletteItemType[]): SearchPaletteItemType[] {
  const order = new Map(preferredOrder.map((type, index) => [type, index]));
  return [...types].sort((a, b) => (order.get(a) ?? 999) - (order.get(b) ?? 999));
}

function mapToSearchType(value?: OmniSearchType | null): SearchPaletteItemType {
  if (value == null) return "Vehicle";

  switch (value) {
    case OmniSearchType.Lead:
      return "Lead";
    case OmniSearchType.Vehicle:
      return "Vehicle";
    case OmniSearchType.Order:
      return "Order";
    case OmniSearchType.Stock:
      return "Stock";
    case OmniSearchType.User:
      return "User";
    default:
      return "Vehicle";
  }
}

function getItemTypeHoverClasses(type: SearchPaletteItemType | undefined, theme: "light" | "dark"): string {
  const resolvedType = type ?? "Vehicle";

  if (theme === "dark") {
    switch (resolvedType) {
      case "Order":
        return "hover:border-l-purple-400 data-focus:border-l-purple-400 hover:bg-purple-500/10 data-focus:bg-purple-500/10";
      case "Vehicle":
        return "hover:border-l-blue-400 data-focus:border-l-blue-400 hover:bg-blue-500/10 data-focus:bg-blue-500/10";
      case "Stock":
        return "hover:border-l-amber-400 data-focus:border-l-amber-400 hover:bg-amber-500/10 data-focus:bg-amber-500/10";
      case "Lead":
        return "hover:border-l-emerald-400 data-focus:border-l-emerald-400 hover:bg-emerald-500/10 data-focus:bg-emerald-500/10";
      case "User":
        return "hover:border-l-emerald-400 data-focus:border-l-emerald-400 hover:bg-emerald-500/10 data-focus:bg-emerald-500/10";
      default:
        return "hover:border-l-blue-400 data-focus:border-l-blue-400 hover:bg-blue-500/10 data-focus:bg-blue-500/10";
    }
  }

  switch (resolvedType) {
    case "Order":
      return "hover:border-l-purple-500 data-focus:border-l-purple-500 hover:bg-purple-50 data-focus:bg-purple-50";
    case "Vehicle":
      return "hover:border-l-blue-500 data-focus:border-l-blue-500 hover:bg-blue-50 data-focus:bg-blue-50";
    case "Stock":
      return "hover:border-l-amber-500 data-focus:border-l-amber-500 hover:bg-amber-50 data-focus:bg-amber-50";
    case "Lead":
      return "hover:border-l-emerald-500 data-focus:border-l-emerald-500 hover:bg-emerald-50 data-focus:bg-emerald-50";
    case "User":
      return "hover:border-l-emerald-500 data-focus:border-l-emerald-500 hover:bg-emerald-50 data-focus:bg-emerald-50";
    default:
      return "hover:border-l-blue-500 data-focus:border-l-blue-500 hover:bg-blue-50 data-focus:bg-blue-50";
  }
}

function getTypeTabActiveClasses(type: SearchPaletteItemType, theme: "light" | "dark"): string {
  if (theme === "dark") {
    switch (type) {
      case "Order":
        return "border-purple-400 bg-purple-500/10 text-white ring-1 ring-purple-400/40";
      case "Vehicle":
        return "border-blue-400 bg-blue-500/10 text-white ring-1 ring-blue-400/40";
      case "Stock":
        return "border-amber-400 bg-amber-500/10 text-white ring-1 ring-amber-400/40";
      case "Lead":
        return "border-emerald-400 bg-emerald-500/10 text-white ring-1 ring-emerald-400/40";
      case "User":
        return "border-emerald-400 bg-emerald-500/10 text-white ring-1 ring-emerald-400/40";
      default:
        return "border-blue-400 bg-blue-500/10 text-white ring-1 ring-blue-400/40";
    }
  }

  switch (type) {
    case "Order":
      return "border-purple-300 bg-purple-50 text-purple-700 ring-1 ring-purple-200";
    case "Vehicle":
      return "border-blue-300 bg-blue-50 text-blue-700 ring-1 ring-blue-200";
    case "Stock":
      return "border-amber-300 bg-amber-50 text-amber-700 ring-1 ring-amber-200";
    case "Lead":
      return "border-emerald-300 bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200";
    case "User":
      return "border-emerald-300 bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200";
    default:
      return "border-blue-300 bg-blue-50 text-blue-700 ring-1 ring-blue-200";
  }
}

function getTypeTabIconClasses(type: SearchPaletteItemType, theme: "light" | "dark"): string {
  if (theme === "dark") {
    switch (type) {
      case "Order":
        return "bg-purple-500/20 text-purple-200";
      case "Vehicle":
        return "bg-blue-500/20 text-blue-200";
      case "Stock":
        return "bg-amber-500/20 text-amber-200";
      case "Lead":
        return "bg-emerald-500/20 text-emerald-200";
      case "User":
        return "bg-emerald-500/20 text-emerald-200";
      default:
        return "bg-blue-500/20 text-blue-200";
    }
  }

  switch (type) {
    case "Order":
      return "bg-purple-100 text-purple-700";
    case "Vehicle":
      return "bg-blue-100 text-blue-700";
    case "Stock":
      return "bg-amber-100 text-amber-700";
    case "Lead":
      return "bg-emerald-100 text-emerald-700";
    case "User":
      return "bg-emerald-100 text-emerald-700";
    default:
      return "bg-blue-100 text-blue-700";
  }
}

function getItemTypeIconSquareClasses(type: SearchPaletteItemType | undefined, theme: "light" | "dark"): string {
  const resolvedType = type ?? "Vehicle";

  if (theme === "dark") {
    switch (resolvedType) {
      case "Order":
        return "bg-purple-500/15 text-purple-300";
      case "Vehicle":
        return "bg-blue-500/15 text-blue-300";
      case "Stock":
        return "bg-amber-500/15 text-amber-300";
      case "Lead":
        return "bg-emerald-500/15 text-emerald-300";
      case "User":
        return "bg-emerald-500/15 text-emerald-300";
      default:
        return "bg-blue-500/15 text-blue-300";
    }
  }

  switch (resolvedType) {
    case "Order":
      return "bg-purple-100 text-purple-700";
    case "Vehicle":
      return "bg-blue-100 text-blue-700";
    case "Stock":
      return "bg-amber-100 text-amber-700";
    case "Lead":
      return "bg-emerald-100 text-emerald-700";
    case "User":
      return "bg-emerald-100 text-emerald-700";
    default:
      return "bg-blue-100 text-blue-700";
  }
}

function getSchemeColour(scheme: "VAT" | "GLOBAL"): StatusPillColour {
  return scheme === "VAT" ? StatusPillColour.Green : StatusPillColour.Blue;
}

function mapToOmniSearchType(value: SearchPaletteItemType | null): OmniSearchType | null {
  if (value == null) return null;

  switch (value) {
    case "Lead":
      return OmniSearchType.Lead;
    case "Vehicle":
      return OmniSearchType.Vehicle;
    case "Order":
      return OmniSearchType.Order;
    case "Stock":
      return OmniSearchType.Stock;
    case "User":
      return OmniSearchType.User;
    default:
      return null;
  }
}

function omniSearchTypeToLabel(value: OmniSearchType): string {
  switch (value) {
    case OmniSearchType.Lead:
      return "Lead";
    case OmniSearchType.Vehicle:
      return "Vehicle";
    case OmniSearchType.Order:
      return "Order";
    case OmniSearchType.Stock:
      return "Stock";
    case OmniSearchType.User:
      return "User";
    default:
      return "";
  }
}
